<template>
  <div class="home-page">
    <section class="home-hero">
      <div class="hero-copy">
        <span class="eyebrow">SMART HARDWARE · DIGITAL EXPERIENCE</span>
        <h1>{{ copy.heroTitle }}</h1>
        <p>{{ copy.heroDescription }}</p>
        <div class="hero-actions">
          <router-link to="/products" class="primary-button">{{ copy.explore }}</router-link>
          <router-link to="/about" class="text-button">{{ copy.about }} <span>→</span></router-link>
        </div>
        <div class="hero-metrics">
          <div><strong>10+</strong><span>{{ copy.metricProducts }}</span></div>
          <div><strong>4</strong><span>{{ copy.metricApps }}</span></div>
          <div><strong>AI</strong><span>{{ copy.metricPlatform }}</span></div>
        </div>
      </div>
      <div class="hero-showcase">
        <div class="showcase-card showcase-main">
          <span>S02 PRO</span>
          <img src="/images/products/s02.jpg" alt="S02 PRO">
        </div>
        <div class="showcase-card showcase-secondary">
          <span>S03 PRO</span>
          <img src="/images/products/s03.jpg" alt="S03 PRO">
        </div>
      </div>
    </section>

    <section class="product-section">
      <div class="section-heading">
        <div><span class="eyebrow">FEATURED PRODUCTS</span><h2>{{ copy.productTitle }}</h2></div>
        <router-link to="/products">{{ copy.viewAll }} →</router-link>
      </div>
      <div class="product-grid">
        <router-link v-for="product in featuredProducts" :key="product.link" :to="product.link" class="product-card">
          <div class="product-image"><img :src="product.image" :alt="product.name"></div>
          <div class="product-info"><span>{{ product.category }}</span><h3>{{ product.name }}</h3><b>↗</b></div>
        </router-link>
      </div>
    </section>

    <section class="capability-section">
      <div class="capability-copy">
        <span class="eyebrow">CONNECTED ECOSYSTEM</span>
        <h2>{{ copy.capabilityTitle }}</h2>
        <p>{{ copy.capabilityDescription }}</p>
      </div>
      <div class="capability-list">
        <router-link to="/levue"><span>01</span><div><h3>Le-Vue</h3><p>{{ copy.levue }}</p></div><b>→</b></router-link>
        <router-link to="/lesound"><span>02</span><div><h3>Le-Sound</h3><p>{{ copy.lesound }}</p></div><b>→</b></router-link>
        <router-link to="/lebond"><span>03</span><div><h3>LeBond</h3><p>{{ copy.lebond }}</p></div><b>→</b></router-link>
      </div>
    </section>

    <section class="home-cta">
      <div><span class="eyebrow">LET'S BUILD WHAT'S NEXT</span><h2>{{ copy.ctaTitle }}</h2></div>
      <router-link to="/contact" class="primary-button">{{ copy.contact }}</router-link>
    </section>
  </div>
</template>

<script>
const zhCopy = {
  heroTitle: '让智能硬件，拥有更自然的体验',
  heroDescription: '从精密智能穿戴到 AI 配套应用，我们连接硬件、软件与真实场景，打造可靠且易用的数字产品体验。',
  explore: '探索产品', about: '了解我们', metricProducts: '产品与方案', metricApps: '智能应用', metricPlatform: '能力平台',
  productTitle: '精选智能产品', viewAll: '查看全部',
  capabilityTitle: '从设备连接，到场景智能',
  capabilityDescription: '统一的应用生态连接智能眼镜、耳机与 AI 玩具，让设备管理、智能交互和数据服务自然协同。',
  levue: '智能眼镜交互与视觉服务', lesound: '眼镜与耳机的一站式设备管理', lebond: '面向 AI 玩具的智能陪伴体验',
  ctaTitle: '一起创造更好的智能产品', contact: '联系我们'
};
const enCopy = {
  heroTitle: 'Making smart hardware feel more natural',
  heroDescription: 'From precision wearables to companion AI apps, we connect hardware, software, and real-world scenarios into reliable digital experiences.',
  explore: 'Explore products', about: 'About us', metricProducts: 'Products & solutions', metricApps: 'Smart apps', metricPlatform: 'Capability platform',
  productTitle: 'Featured smart products', viewAll: 'View all',
  capabilityTitle: 'From device connection to contextual intelligence',
  capabilityDescription: 'A connected app ecosystem brings smart glasses, earbuds, and AI toys together through effortless management, interaction, and data services.',
  levue: 'Smart-glasses interaction and visual services', lesound: 'Unified management for glasses and earbuds', lebond: 'Intelligent companionship for AI toys',
  ctaTitle: 'Let’s create better smart products', contact: 'Contact us'
};

export default {
  name: 'Home',
  computed: {
    isEnglish() { return this.$i18n.locale.indexOf('en') !== -1; },
    copy() { return this.isEnglish ? enCopy : zhCopy; },
    featuredProducts() {
      const category = this.isEnglish ? 'SMART WEARABLE' : '智能穿戴';
      const movement = this.isEnglish ? 'MECHANICAL SERIES' : '机械系列';
      return [
        { name: 'S02 PRO', image: '/images/products/s02.jpg', link: '/products/s02', category },
        { name: 'S03 PRO', image: '/images/products/s03.jpg', link: '/products/s03', category },
        { name: this.$t('products.productList[1]'), image: '/images/products/yx1.png', link: '/products/yx/1', category: movement }
      ];
    }
  },
  mounted() { document.body.className = 'site-page'; }
};
</script>

<style lang="stylus" scoped>
.home-page
  color #101b38
  background #f6f8fc

.home-hero
  max-width 1440px
  min-height 700px
  margin 0 auto
  padding 82px 48px 96px
  display grid
  grid-template-columns minmax(360px, .86fr) minmax(560px, 1.14fr)
  align-items center
  gap 72px

.eyebrow
  display inline-block
  color #3769db
  font-size 11px
  font-weight 750
  letter-spacing .18em

.hero-copy
  h1
    max-width 640px
    margin 22px 0
    font-size clamp(48px, 5.8vw, 82px)
    line-height 1.07
    letter-spacing -.055em
  > p
    max-width 600px
    margin 0
    color #61708d
    font-size 17px
    line-height 1.85

.hero-actions
  display flex
  align-items center
  gap 24px
  margin-top 34px

.primary-button
  display inline-flex
  align-items center
  justify-content center
  min-height 50px
  padding 0 25px
  color #fff
  background #2257d7
  border-radius 999px
  text-decoration none
  font-weight 650
  box-shadow 0 14px 30px rgba(34,87,215,.24)
  transition transform .25s ease, box-shadow .25s ease
  &:hover
    transform translateY(-2px)
    box-shadow 0 18px 38px rgba(34,87,215,.3)

.text-button
  color #172544
  text-decoration none
  font-weight 650
  span
    margin-left 7px

.hero-metrics
  display flex
  gap 36px
  margin-top 52px
  padding-top 26px
  border-top 1px solid #dde3ef
  div
    display flex
    flex-direction column
  strong
    font-size 24px
    color #1b4fc7
  span
    margin-top 4px
    color #7a869d
    font-size 12px

.hero-showcase
  min-height 510px
  position relative
  &::before
    content ''
    position absolute
    inset 8% 3% 1% 8%
    border-radius 50%
    background linear-gradient(145deg, rgba(70,120,242,.23), rgba(74,218,234,.12))
    filter blur(35px)

.showcase-card
  position absolute
  overflow hidden
  border 1px solid rgba(104,130,197,.15)
  border-radius 30px
  background rgba(255,255,255,.86)
  box-shadow 0 30px 80px rgba(35,55,104,.18)
  span
    position absolute
    z-index 1
    left 24px
    top 22px
    color #1d315f
    font-size 12px
    font-weight 750
    letter-spacing .12em
  img
    width 100%
    height 100%
    object-fit contain

.showcase-main
  width 72%
  height 76%
  right 0
  top 0
.showcase-secondary
  width 56%
  height 55%
  left 0
  bottom 0

.product-section
  max-width 1440px
  margin 0 auto
  padding 110px 48px

.section-heading
  display flex
  justify-content space-between
  align-items flex-end
  gap 24px
  margin-bottom 38px
  h2
    margin 12px 0 0
    font-size clamp(34px, 4vw, 52px)
    letter-spacing -.04em
  > a
    color #325fc9
    text-decoration none
    font-weight 650

.product-grid
  display grid
  grid-template-columns repeat(3, minmax(0,1fr))
  gap 24px

.product-card
  min-width 0
  color inherit
  background #fff
  border 1px solid #e5e9f2
  border-radius 24px
  overflow hidden
  text-decoration none
  transition transform .3s ease, box-shadow .3s ease
  &:hover
    transform translateY(-6px)
    box-shadow 0 24px 55px rgba(28,48,101,.13)

.product-image
  height 330px
  padding 20px
  background linear-gradient(145deg,#f7f9fd,#edf2fa)
  img
    width 100%
    height 100%
    object-fit contain

.product-info
  min-height 118px
  padding 22px 24px
  position relative
  span
    color #7987a2
    font-size 10px
    letter-spacing .14em
  h3
    margin 8px 40px 0 0
    font-size 20px
  b
    position absolute
    right 24px
    bottom 25px
    color #2d61d2

.capability-section
  padding 110px 48px
  display grid
  grid-template-columns .8fr 1.2fr
  gap 90px
  color #edf3ff
  background #0b1430

.capability-copy
  h2
    margin 18px 0
    font-size clamp(36px,4vw,58px)
    line-height 1.15
    letter-spacing -.045em
  p
    margin 0
    color #a9b5ce
    font-size 16px
    line-height 1.85
  .eyebrow
    color #73d9ff

.capability-list
  border-top 1px solid rgba(255,255,255,.12)
  a
    min-height 126px
    display grid
    grid-template-columns 48px 1fr auto
    align-items center
    gap 20px
    color inherit
    border-bottom 1px solid rgba(255,255,255,.12)
    text-decoration none
    transition padding .25s ease, background .25s ease
    &:hover
      padding 0 15px
      background rgba(255,255,255,.04)
    > span
      color #647498
      font-size 12px
    h3
      margin 0 0 7px
      font-size 23px
    p
      margin 0
      color #8f9cb8
      font-size 14px
    b
      color #73d9ff

.home-cta
  max-width 1440px
  margin 0 auto
  padding 100px 48px
  display flex
  justify-content space-between
  align-items center
  gap 30px
  h2
    margin 12px 0 0
    font-size clamp(34px,4vw,54px)
    letter-spacing -.045em

@media (max-width: 960px)
  .home-hero
    grid-template-columns 1fr
    padding 64px 30px 82px
  .hero-showcase
    min-height 500px
  .product-section
    padding 84px 30px
  .product-grid
    grid-template-columns repeat(2,minmax(0,1fr))
  .product-card:last-child
    grid-column 1/-1
  .capability-section
    padding 84px 30px
    grid-template-columns 1fr
    gap 52px

@media (max-width: 640px)
  .home-hero
    padding 50px 20px 64px
    gap 44px
  .hero-copy h1
    font-size 44px
  .hero-metrics
    gap 18px
    justify-content space-between
  .hero-showcase
    min-height 350px
  .showcase-card
    border-radius 20px
  .product-section
    padding 70px 20px
  .section-heading
    align-items flex-start
    flex-direction column
  .product-grid
    grid-template-columns 1fr
  .product-card:last-child
    grid-column auto
  .product-image
    height 280px
  .capability-section
    padding 70px 20px
  .capability-list a
    grid-template-columns 34px 1fr auto
  .home-cta
    padding 70px 20px
    align-items flex-start
    flex-direction column
</style>
